import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getToken } from '../utils/auth';

const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const AccountingAuditReport = () => {
  const [postedRecords, setPostedRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [accountingPeriods, setAccountingPeriods] = useState([]);
  const [projects, setProjects] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  
  // Filter states
  const [selectedTransactionType, setSelectedTransactionType] = useState('all');
  const [selectedProject, setSelectedProject] = useState('all');
  const [selectedPeriod, setSelectedPeriod] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedIntegration, setSelectedIntegration] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Sort state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  // Reconcile state
  const [reconciling, setReconciling] = useState(false);
  const [reconcileResults, setReconcileResults] = useState(null);

  const fetchReportData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = getToken();
      const params = {};
      
      // Only add params if they are not 'all'
      if (selectedTransactionType && selectedTransactionType !== 'all') {
        params.transaction_type = selectedTransactionType;
      }
      if (selectedProject && selectedProject !== 'all') {
        params.project_vuid = selectedProject;
      }
      if (selectedPeriod && selectedPeriod !== 'all') {
        params.accounting_period_vuid = selectedPeriod;
      }
      if (selectedStatus && selectedStatus !== 'all') {
        params.status = selectedStatus;
      }
      if (selectedIntegration && selectedIntegration !== 'all') {
        params.integration_vuid = selectedIntegration;
      }
      
      console.log('Fetching report data with params:', params);
      
      const response = await axios.get(`${baseURL}/api/accounting-audit-report`, {
        headers: { 'Authorization': `Bearer ${token}` },
        params
      });
      
      console.log('Received records:', response.data.posted_records?.length || 0);
      setPostedRecords(response.data.posted_records || []);
    } catch (error) {
      console.error('Error fetching audit report:', error);
      setError('Failed to fetch audit report data');
    } finally {
      setLoading(false);
    }
  }, [selectedTransactionType, selectedProject, selectedPeriod, selectedStatus, selectedIntegration]);

  useEffect(() => {
    fetchAccountingPeriods();
    fetchProjects();
    fetchIntegrations();
  }, []);

  useEffect(() => {
    fetchReportData();
    // Clear reconciliation results when filters change (they're specific to the filtered set)
    setReconcileResults(null);
  }, [fetchReportData]);

  const fetchAccountingPeriods = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${baseURL}/api/accounting-periods`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setAccountingPeriods(response.data.sort((a, b) => {
        if (a.year !== b.year) return b.year - a.year;
        return b.month - a.month;
      }));
    } catch (error) {
      console.error('Error fetching accounting periods:', error);
    }
  };

  const fetchProjects = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${baseURL}/api/projects`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setProjects(response.data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchIntegrations = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${baseURL}/api/integrations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setIntegrations(response.data.filter(i => i.status === 'active') || []);
    } catch (error) {
      console.error('Error fetching integrations:', error);
    }
  };


  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = (data) => {
    if (!sortConfig.key) return data;
    
    return [...data].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];
      
      // Handle nested properties
      if (sortConfig.key === 'project') {
        aVal = a.project?.project_name || '';
        bVal = b.project?.project_name || '';
      } else if (sortConfig.key === 'accounting_period') {
        aVal = a.accounting_period ? `${a.accounting_period.year}-${String(a.accounting_period.month).padStart(2, '0')}` : '';
        bVal = b.accounting_period ? `${b.accounting_period.year}-${String(b.accounting_period.month).padStart(2, '0')}` : '';
      } else if (sortConfig.key === 'posted_at') {
        // Properly parse dates for sorting
        aVal = a.posted_at ? new Date(a.posted_at).getTime() : 0;
        bVal = b.posted_at ? new Date(b.posted_at).getTime() : 0;
      } else if (sortConfig.key === 'description') {
        aVal = a.description || '';
        bVal = b.description || '';
      }
      
      // Handle null/undefined values
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';
      
      // Handle numeric values
      if (['total_amount', 'net_amount', 'retainage_amount', 'total_debits', 'total_credits'].includes(sortConfig.key)) {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
        // For numeric values, return directly
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      // Handle date values (already converted to timestamps)
      if (sortConfig.key === 'posted_at') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }
      
      // Handle external_ids (sort by existence, then by first external ID value)
      if (sortConfig.key === 'external_ids') {
        const aHasIds = a.external_ids && a.external_ids.length > 0;
        const bHasIds = b.external_ids && b.external_ids.length > 0;
        
        // First sort by existence
        if (aHasIds !== bHasIds) {
          return sortConfig.direction === 'asc' ? (aHasIds ? -1 : 1) : (aHasIds ? 1 : -1);
        }
        
        // Both have same existence status, sort by first external ID value
        if (aHasIds && bHasIds) {
          const aFirstId = (a.external_ids[0]?.external_id || '').toLowerCase();
          const bFirstId = (b.external_ids[0]?.external_id || '').toLowerCase();
          if (aFirstId < bFirstId) {
            return sortConfig.direction === 'asc' ? -1 : 1;
          }
          if (aFirstId > bFirstId) {
            return sortConfig.direction === 'asc' ? 1 : -1;
          }
        }
        return 0;
      }
      
      // String comparison
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
        if (aVal < bVal) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      }
      
      // Fallback comparison
      if (aVal < bVal) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aVal > bVal) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    
    if (sortConfig.direction === 'asc') {
      return (
        <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
      );
    }
    
    return (
      <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  const filteredRecords = postedRecords.filter(record => {
    const matchesSearch = !searchTerm || 
      record.reference_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.project?.project_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      record.project?.project_number?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  const sortedRecords = getSortedData(filteredRecords);

  const getTransactionTypeLabel = (type, record) => {
    // Check if this is a retainage entry
    const isRetainage = record?.reference_type === 'Retainage' || record?.journal_suffix === '-R';
    const isRetentionRelease = record?.journal_suffix === '-RR';
    
    switch (type) {
      case 'ap_invoice': 
        if (isRetentionRelease) return 'AP Invoice - Retention Release';
        if (isRetainage) return 'AP Invoice - Retainage';
        return 'AP Invoice';
      case 'project_billing': 
        if (isRetentionRelease) return 'Project Billing - Retention Release';
        if (isRetainage) return 'Project Billing - Retainage';
        return 'Project Billing';
      case 'labor_cost': return 'Labor Cost';
      case 'project_expense': return 'Project Expense';
      case 'under_billing': return 'Under Billing';
      case 'over_billing': return 'Over Billing';
      case 'ap_invoice_payment': return 'AP Invoice Payment';
      default: return type;
    }
  };

  const getTransactionTypeColor = (type) => {
    const colors = {
      'ap_invoice': 'bg-blue-100 text-blue-800',
      'project_billing': 'bg-green-100 text-green-800',
      'labor_cost': 'bg-yellow-100 text-yellow-800',
      'project_expense': 'bg-purple-100 text-purple-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const syncedCount = filteredRecords.filter(r => r.is_synced).length;
  const notSyncedCount = filteredRecords.filter(r => !r.is_synced).length;
  // Calculate total amount - use total_amount directly (now correctly matches line items sum after our fix)
  // For split entries (AP invoices/Project Billings with retainage), each PostedRecord is separate:
  // - Bill/Invoice record has total_amount = net amount (from line items)
  // - Retainage record has total_amount = retainage amount
  // So we just sum all total_amount values
  const totalAmount = filteredRecords.reduce((sum, r) => {
    // Use total_amount directly (it now correctly matches the sum of line items)
    return sum + (r.total_amount || 0);
  }, 0);
  const reconcileAlertCount = filteredRecords.filter(r => r.has_reconcile_alert).length;

  const handleReconcile = async () => {
    if (!selectedIntegration || selectedIntegration === 'all') {
      alert('Please select a specific integration to reconcile');
      return;
    }

    // Get all synced records from filtered results
    const syncedRecords = filteredRecords.filter(r => r.is_synced);
    
    if (syncedRecords.length === 0) {
      alert('No synced records found in the current filter to reconcile');
      return;
    }

    setReconciling(true);
    setReconcileResults(null);
    setError(null);

    try {
      const token = getToken();
      const response = await axios.post(
        `${baseURL}/api/accounting-audit-report/reconcile`,
        {
          posted_record_vuids: syncedRecords.map(r => r.vuid),
          integration_vuid: selectedIntegration
        },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setReconcileResults(response.data);
        // Refresh the report data to show updated reconcile status
        await fetchReportData();
        
        const { verified, missing, amount_mismatches, errors, total_local_amount, total_qbo_amount, total_discrepancy } = response.data.summary;
        let message = `Reconciliation complete:\n`;
        message += `✓ Verified: ${verified}\n`;
        if (missing > 0) {
          message += `⚠ Missing in QBO: ${missing}\n`;
        }
        if (amount_mismatches > 0) {
          message += `⚠ Amount Mismatches: ${amount_mismatches}\n`;
        }
        if (errors > 0) {
          message += `✗ Errors: ${errors}\n`;
        }
        if (total_local_amount !== undefined && total_qbo_amount !== undefined) {
          message += `\nTotal Amounts:\n`;
          message += `  Claret: $${total_local_amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\n`;
          message += `  QBO: $${total_qbo_amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\n`;
          if (Math.abs(total_discrepancy) > 0.01) {
            message += `  Discrepancy: $${total_discrepancy.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          }
        }
        alert(message);
      } else {
        setError(response.data.error || 'Reconciliation failed');
      }
    } catch (error) {
      console.error('Error reconciling:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.details || error.message || 'Failed to reconcile transactions';
      setError(errorMessage);
      console.error('Reconciliation error details:', error.response?.data);
    } finally {
      setReconciling(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Accounting Audit Report</h1>
        <p className="text-gray-600">
          Review all posted transactions and verify their sync status with external ERP systems
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Record Type
            </label>
            <select
              value={selectedTransactionType}
              onChange={(e) => setSelectedTransactionType(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Types</option>
              <option value="ap_invoice">AP Invoice</option>
              <option value="project_billing">Project Billing</option>
              <option value="labor_cost">Labor Cost</option>
              <option value="project_expense">Project Expense</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Project
            </label>
            <select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Projects</option>
              {projects.map(project => (
                <option key={project.vuid} value={project.vuid}>
                  {project.project_number} - {project.project_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Accounting Period
            </label>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Periods</option>
              {accountingPeriods.map(period => (
                <option key={period.vuid} value={period.vuid}>
                  {period.month}/{period.year} ({period.status})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="synced">Synced</option>
              <option value="not_synced">Not Synced</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Integration
            </label>
            <select
              value={selectedIntegration}
              onChange={(e) => setSelectedIntegration(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Integrations</option>
              {integrations.map(integration => (
                <option key={integration.vuid} value={integration.vuid}>
                  {integration.integration_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{filteredRecords.length}</div>
            <div className="text-sm text-gray-600">Total Posted Records</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{syncedCount}</div>
            <div className="text-sm text-gray-600">Synced</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">{notSyncedCount}</div>
            <div className="text-sm text-gray-600">Not Synced</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">${totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            <div className="text-sm text-gray-600">Total Amount (Claret)</div>
          </div>
        </div>
        
        {/* Reconcile Summary */}
        {reconcileResults && reconcileResults.summary && (
          <div className="border-t pt-4 mt-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Reconciliation Summary</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Claret Total:</span>
                <span className="ml-2 font-semibold">${reconcileResults.summary.total_local_amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <div>
                <span className="text-gray-600">QBO Total:</span>
                <span className="ml-2 font-semibold">${reconcileResults.summary.total_qbo_amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <div>
                <span className="text-gray-600">Discrepancy:</span>
                <span className={`ml-2 font-semibold ${
                  Math.abs(reconcileResults.summary.total_discrepancy || 0) > 0.01 
                    ? 'text-amber-600' 
                    : 'text-green-600'
                }`}>
                  ${(reconcileResults.summary.total_discrepancy || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </div>
        )}
        
        {/* Reconcile Button */}
        {selectedIntegration && selectedIntegration !== 'all' && syncedCount > 0 && (
          <div className="border-t pt-4 mt-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-1">Reconcile with QuickBooks Online</h3>
                <p className="text-xs text-gray-500">
                  Verify that all synced transactions ({syncedCount}) still exist in QuickBooks Online and amounts match
                  {reconcileAlertCount > 0 && (
                    <span className="ml-2 text-amber-600 font-semibold">
                      • {reconcileAlertCount} transaction(s) need attention
                    </span>
                  )}
                </p>
              </div>
              <button
                onClick={handleReconcile}
                disabled={reconciling}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  reconciling
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                {reconciling ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Reconciling...
                  </span>
                ) : (
                  'Reconcile'
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="text-sm text-red-800">{error}</div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <div className="text-gray-600">Loading audit report...</div>
        </div>
      )}

      {/* Table */}
      {!loading && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('posted_at')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Posted Date</span>
                      {getSortIcon('posted_at')}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('transaction_type')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Record Type</span>
                      {getSortIcon('transaction_type')}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('reference_number')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Reference Number</span>
                      {getSortIcon('reference_number')}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('project')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Project</span>
                      {getSortIcon('project')}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('accounting_period')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Period</span>
                      {getSortIcon('accounting_period')}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('total_amount')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Total Amount</span>
                      {getSortIcon('total_amount')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sync Status
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('external_ids')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>External IDs</span>
                      {getSortIcon('external_ids')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reconcile Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedRecords.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="px-6 py-4 text-center text-gray-500">
                      No posted records found matching the selected filters.
                    </td>
                  </tr>
                ) : (
                  sortedRecords.map((record) => (
                    <tr 
                      key={record.vuid} 
                      className={`hover:bg-gray-50 ${
                        record.has_reconcile_alert && record.reconcile_alert_reason === 'amount_mismatch'
                          ? 'bg-amber-50 border-l-4 border-amber-400'
                          : ''
                      }`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {record.posted_at ? new Date(record.posted_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTransactionTypeColor(record.transaction_type)}`}>
                          {getTransactionTypeLabel(record.transaction_type, record)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {record.reference_number || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {record.project ? `${record.project.project_number} - ${record.project.project_name}` : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {record.accounting_period ? `${record.accounting_period.month}/${record.accounting_period.year}` : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        ${(record.total_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {record.is_synced ? (
                          <div className="flex items-center gap-2">
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                              Synced ({record.sync_count})
                            </span>
                            {record.has_reconcile_alert && (
                              <div className="flex flex-col gap-1">
                                <span 
                                  className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-800"
                                  data-tooltip={
                                    record.reconcile_alert_reason === 'amount_mismatch' 
                                      ? `Amount mismatch: Local=$${record.reconcile_local_amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || record.total_amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}, QBO=$${record.reconcile_qbo_amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}, Diff=$${record.reconcile_amount_discrepancy?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                      : "This transaction was not found in QuickBooks Online during the last reconciliation"
                                  }
                                >
                                  {record.reconcile_alert_reason === 'amount_mismatch' ? '⚠️ Amount Mismatch' : '⚠️ Missing in QBO'}
                                </span>
                                {record.reconcile_alert_reason === 'amount_mismatch' && record.reconcile_amount_discrepancy && (
                                  <div className="text-xs text-amber-700 font-semibold">
                                    Diff: ${Math.abs(record.reconcile_amount_discrepancy).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    {record.reconcile_local_amount && record.reconcile_qbo_amount && (
                                      <span className="text-gray-600 font-normal ml-2">
                                        ({record.reconcile_local_amount > record.reconcile_qbo_amount ? 'Claret higher' : 'QBO higher'})
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                            Not Synced
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {record.external_ids && record.external_ids.length > 0 ? (
                          <div className="space-y-1">
                            {record.external_ids.map((extId, idx) => (
                              <div key={idx} className="flex items-start gap-2 mb-2">
                                <div className="flex flex-col">
                                  <span className="font-medium text-sm">{extId.integration_name}</span>
                                  <div className="flex items-center gap-1">
                                    {extId.qbo_url ? (
                                      <a
                                        href={extId.qbo_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:text-blue-800 underline text-sm"
                                      >
                                        {extId.external_id}
                                      </a>
                                    ) : (
                                      <span className="text-sm">{extId.external_id}</span>
                                    )}
                                    <span className="text-xs text-gray-500">
                                      ({extId.external_object_type})
                                    </span>
                                  </div>
                                </div>
                                {extId.reconcile_status && (
                                  <div className="flex flex-col gap-1">
                                    {extId.reconcile_status === 'verified' ? (
                                      <span className="text-green-600 text-sm" data-tooltip="Verified in QBO">✓</span>
                                    ) : extId.reconcile_status === 'missing' ? (
                                      <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">⚠ Missing</span>
                                    ) : extId.reconcile_status === 'amount_mismatch' ? (
                                      <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-700">⚠ Amount Mismatch</span>
                                    ) : (
                                      <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">{extId.reconcile_status}</span>
                                    )}
                                    {extId.reconcile_amount_discrepancy && extId.reconcile_amount_discrepancy > 0.01 && (
                                      <div className="text-xs text-amber-700 border border-amber-300 rounded px-2 py-1 bg-amber-50">
                                        <div className="font-semibold mb-1">Amount Discrepancy:</div>
                                        <div>Claret: ${extId.reconcile_local_amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                        <div>QBO: ${extId.reconcile_qbo_amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                        <div className="font-bold text-amber-800 mt-1">
                                          Difference: ${extId.reconcile_amount_discrepancy.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                          {extId.reconcile_local_amount && extId.reconcile_qbo_amount && (
                                            <span className="font-normal text-amber-700 ml-1">
                                              ({extId.reconcile_local_amount > extId.reconcile_qbo_amount ? 'Claret higher' : 'QBO higher'})
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-400">No external IDs</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        {(() => {
                          // Check reconcile status from external_ids
                          // For AP invoices, we have both 'ap_invoice' (bill) and 'journal_entry' (retainage) mappings
                          // For project billings, we have both 'project_billing' (invoice) and 'journal_entry' (retainage) mappings
                          // The reconcile status should be the same across all mappings for a given record
                          // So we can check any mapping that has a reconcile_status
                          const reconcileStatus = record.external_ids?.find(e => e.reconcile_status)?.reconcile_status;
                          
                          // Debug logging to help diagnose issues
                          if (!reconcileStatus && record.is_synced && record.external_ids && record.external_ids.length > 0) {
                            console.log(`[Reconcile Status Debug] Record ${record.reference_number}: No reconcile_status found. External IDs:`, 
                              record.external_ids.map(e => ({
                                object_type: e.object_type,
                                external_id: e.external_id,
                                reconcile_status: e.reconcile_status,
                                reconcile_alert: e.reconcile_alert,
                                reconcile_alert_reason: e.reconcile_alert_reason,
                                has_metadata: !!e.external_metadata,
                                metadata_keys: e.external_metadata ? Object.keys(e.external_metadata) : []
                              }))
                            );
                          }
                          
                          if (reconcileStatus === 'amount_mismatch') {
                            return (
                              <div className="flex flex-col gap-1">
                                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                  ⚠️ Amount Mismatch
                                </span>
                                {record.reconcile_amount_discrepancy && (
                                  <div className="text-xs text-red-700 font-semibold">
                                    ${Math.abs(record.reconcile_amount_discrepancy).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} difference
                                  </div>
                                )}
                                {record.reconcile_local_amount && record.reconcile_qbo_amount && (
                                  <div className="text-xs text-gray-600">
                                    <div>Claret: ${record.reconcile_local_amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                    <div>QBO: ${record.reconcile_qbo_amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                                  </div>
                                )}
                              </div>
                            );
                          } else if (reconcileStatus === 'missing') {
                            return (
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-800">
                                ⚠️ Missing in QBO
                              </span>
                            );
                          } else if (reconcileStatus === 'verified') {
                            return (
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                ✓ Verified
                              </span>
                            );
                          } else if (reconcileStatus === undefined || reconcileStatus === null) {
                            // No reconcile status means reconciliation hasn't been run yet
                            if (record.is_synced) {
                              return (
                                <span className="text-xs text-gray-400">Synced (not reconciled)</span>
                              );
                            } else {
                              return (
                                <span className="text-xs text-gray-400">Not reconciled</span>
                              );
                            }
                          } else {
                            // Unknown status
                            return (
                              <span className="text-xs text-gray-400">{reconcileStatus}</span>
                            );
                          }
                        })()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountingAuditReport;

